import type { Editor } from '@tiptap/core';
import { useEditorState } from '@tiptap/react';
import { createContext, type ReactNode, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type Destination, useDestinations } from '@/features/quick-nav';
import { absoluteUrl } from '@/shared/lib/internal-links';
import { useIsMobile, useIsTouchUi } from '@/shared/lib/media';
import {
  Button,
  Combobox,
  type ComboboxOption,
  Dialog,
  DialogActions,
  MenuItem,
  MenuList,
  MenuRow,
  Popover,
  TextField,
} from '@/shared/ui';

import {
  COLORS,
  DEFAULT_FONT_FAMILY,
  FONT_FAMILIES,
  formatFontSize,
  HIGHLIGHTS,
  isSafeLink,
  LINE_HEIGHTS,
  MAX_FONT_SIZE,
  MAX_INDENT,
  MIN_FONT_SIZE,
  SPACE_AFTER,
  selectionFontSize,
} from './extensions';
import { FontSizeField } from './FontSizeField';
import { ToolIcons } from './toolbar-icons';
import styles from './DocumentEditor.module.css';

/** A translation under `documents.editor.` whose key is built at run time. */
type Tr = (key: string, options?: Record<string, unknown>) => string;

/**
 * Inside the phone's "Format" panel a menu / palette is drawn open in place: a popover inside a
 * popover would close its parent on the first press.
 */
const InPanel = createContext(false);

/**
 * Called after a popover choice closed it. The popover hands focus back to its trigger; on a
 * desktop the editor takes it from there (typing goes on), on a touch screen focus is left alone
 * so that merely picking an option never raises the on-screen keyboard.
 */
const Settle = createContext<() => void>(() => undefined);

/** Keep the document's selection: a press on the toolbar never moves it. */
const keepSelection = (event: { preventDefault: () => void }) => {
  event.preventDefault();
};

function Tool({
  label,
  active,
  disabled,
  onRun,
  children,
  testId,
  wide,
}: {
  label: string;
  active?: boolean | undefined;
  disabled?: boolean;
  onRun: () => void;
  children: ReactNode;
  testId?: string;
  /** A glyph made of letters (B, I) rather than an icon. */
  wide?: boolean;
}) {
  return (
    <button
      type="button"
      className={styles.tool}
      aria-label={label}
      title={label}
      aria-pressed={active ?? undefined}
      disabled={disabled}
      data-testid={testId}
      data-letter={wide || undefined}
      onMouseDown={keepSelection}
      onClick={onRun}
    >
      {children}
    </button>
  );
}

/** A group of related tools: a small caption (desktop) and one visual unit. */
function Group({ label, children, grow }: { label: string; children: ReactNode; grow?: boolean }) {
  return (
    <div className={styles.group} role="group" aria-label={label} data-grow={grow || undefined}>
      {children}
    </div>
  );
}

/** A tool that opens a menu of choices; `items` get `close` so a choice dismisses it. */
function MenuTool({
  label,
  icon,
  testId,
  width = 240,
  children,
}: {
  label: string;
  icon: ReactNode;
  testId?: string;
  width?: number;
  children: (close: () => void) => ReactNode;
}) {
  const inPanel = useContext(InPanel);
  const settle = useContext(Settle);
  if (inPanel) {
    return (
      <section className={styles.panelSection} aria-label={label} data-testid={testId}>
        <h3 className={styles.panelHeading}>{label}</h3>
        {children(() => undefined)}
      </section>
    );
  }
  return (
    <Popover
      label={label}
      width={width}
      trigger={(props, open) => (
        <button
          type="button"
          {...props}
          className={styles.tool}
          data-wide
          aria-label={label}
          title={label}
          data-open={open || undefined}
          data-testid={testId}
          onMouseDown={keepSelection}
        >
          {icon}
          <ToolIcons.chevron />
        </button>
      )}
    >
      {(close) =>
        children(() => {
          close();
          settle();
        })
      }
    </Popover>
  );
}

export function LinkDialog({ editor, onClose }: { editor: Editor; onClose: () => void }) {
  const { t } = useTranslation();
  const current = (editor.getAttributes('link') as { href?: string }).href ?? '';
  const [url, setUrl] = useState(current);
  const [error, setError] = useState(false);
  // A Planosfera item chosen from the list: its address goes into the field, and its name becomes
  // the text when nothing is selected in the note.
  const [picked, setPicked] = useState<Destination | null>(null);
  const { destinations } = useDestinations();
  const options: ComboboxOption[] = destinations
    .filter((destination) => destination.kind !== 'group')
    .map((destination) => ({
      value: destination.key,
      label: destination.name,
      description: [t(`quickNav.kinds.${destination.kind}`), destination.context]
        .filter(Boolean)
        .join(' · '),
    }));
  return (
    <Dialog title={t('documents.editor.link')} onClose={onClose}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          const value = url.trim();
          const chain = editor.chain().focus().extendMarkRange('link');
          if (value === '') {
            chain.unsetLink().run();
            onClose();
            return;
          }
          const href = /^[a-z][a-z0-9+.-]*:/i.test(value) ? value : `https://${value}`;
          if (!isSafeLink(href)) {
            setError(true);
            return;
          }
          if (picked && editor.state.selection.empty && current === '') {
            chain
              .insertContent({
                type: 'text',
                text: picked.name,
                marks: [{ type: 'link', attrs: { href } }],
              })
              .run();
          } else {
            chain.setLink({ href }).run();
          }
          onClose();
        }}
      >
        <Combobox
          label={t('links.toResource')}
          value={picked?.key ?? null}
          options={options}
          placeholder={t('links.pick')}
          searchPlaceholder={t('common.search')}
          emptyText={t('common.noResults')}
          onChange={(key) => {
            const destination = destinations.find((item) => item.key === key) ?? null;
            setPicked(destination);
            if (destination) {
              setUrl(absoluteUrl(destination.to));
              setError(false);
            }
          }}
        />
        <p className={styles.linkHint}>{t('links.pickHint')}</p>
        <TextField
          label={t('documents.editor.linkUrl')}
          value={url}
          autoFocus
          inputMode="url"
          error={error ? t('documents.editor.linkInvalid') : undefined}
          data-testid="link-url"
          onChange={(event) => {
            setUrl(event.target.value);
            setPicked(null);
            setError(false);
          }}
        />
        <DialogActions>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary">
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

/** A colour tool: a swatch palette in a popover (or open in place inside the compact panel). */
function Swatches({
  label,
  colors,
  current,
  onPick,
  onClear,
  children,
  testId,
}: {
  label: string;
  colors: readonly string[];
  current: string | null;
  onPick: (color: string) => void;
  onClear: () => void;
  children: ReactNode;
  testId: string;
}) {
  const { t } = useTranslation();
  const inPanel = useContext(InPanel);
  const settle = useContext(Settle);
  const palette = (close: () => void) => (
    <div className={styles.swatches} role="group" aria-label={label}>
      {colors.map((color) => (
        <button
          key={color}
          type="button"
          className={styles.swatch}
          style={{ background: color }}
          aria-label={color}
          aria-pressed={current === color}
          onMouseDown={keepSelection}
          onClick={() => {
            close();
            onPick(color);
          }}
        />
      ))}
      <button
        type="button"
        className={styles.swatchClear}
        onMouseDown={keepSelection}
        onClick={() => {
          close();
          onClear();
        }}
      >
        {t('documents.editor.clearColor')}
      </button>
    </div>
  );
  if (inPanel) {
    return (
      <section className={styles.panelSection} aria-label={label} data-testid={testId}>
        <h3 className={styles.panelHeading}>{label}</h3>
        {palette(() => undefined)}
      </section>
    );
  }
  return (
    <Popover
      label={label}
      width={220}
      trigger={(props, open) => (
        <button
          type="button"
          {...props}
          className={styles.tool}
          data-wide
          aria-label={label}
          title={label}
          data-open={open || undefined}
          data-testid={testId}
          onMouseDown={keepSelection}
        >
          {children}
          <ToolIcons.chevron />
        </button>
      )}
    >
      {(close) =>
        palette(() => {
          close();
          settle();
        })
      }
    </Popover>
  );
}

/** Structure of the table under the cursor; shown only while the cursor is in one. */
function TableBar({ editor, tr }: { editor: Editor; tr: Tr }) {
  const chain = () => editor.chain().focus();
  // merge needs a cell selection of several cells, split needs a merged cell: otherwise disabled
  const canMerge = editor.can().mergeCells();
  const canSplit = editor.can().splitCell();
  const action = (
    label: string,
    run: () => void,
    testId?: string,
    danger?: boolean,
    disabled?: boolean,
  ) => (
    <button
      type="button"
      className={styles.tableAction}
      data-testid={testId}
      data-danger={danger || undefined}
      disabled={disabled}
      onMouseDown={keepSelection}
      onClick={run}
    >
      {tr(`table.${label}`)}
    </button>
  );
  return (
    <div
      className={styles.tableBar}
      role="toolbar"
      aria-label={tr('table.bar')}
      data-testid="table-bar"
    >
      <span className={styles.tableGroup} role="group" aria-label={tr('table.rows')}>
        {action('rowAbove', () => chain().addRowBefore().run())}
        {action('rowBelow', () => chain().addRowAfter().run(), 'table-row-below')}
        {action('deleteRow', () => chain().deleteRow().run(), 'table-delete-row')}
      </span>
      <span className={styles.tableGroup} role="group" aria-label={tr('table.columns')}>
        {action('colLeft', () => chain().addColumnBefore().run())}
        {action('colRight', () => chain().addColumnAfter().run(), 'table-col-right')}
        {action('deleteCol', () => chain().deleteColumn().run(), 'table-delete-col')}
      </span>
      <span className={styles.tableGroup} role="group" aria-label={tr('table.bar')}>
        {action('header', () => chain().toggleHeaderRow().run())}
        {action('merge', () => chain().mergeCells().run(), 'table-merge', false, !canMerge)}
        {action('split', () => chain().splitCell().run(), 'table-split', false, !canSplit)}
        {action('delete', () => chain().deleteTable().run(), 'table-delete', true)}
      </span>
    </div>
  );
}

const BLOCKS = ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const;

function useToolbarState(editor: Editor) {
  return useEditorState({
    editor,
    selector: ({ editor: e }) => {
      const block =
        BLOCKS.find((b) => b !== 'p' && e.isActive('heading', { level: Number(b.slice(1)) })) ??
        'p';
      const textStyle = e.getAttributes('textStyle') as {
        color?: string;
        fontFamily?: string;
        fontSize?: string;
      };
      const paragraph = e.getAttributes(block === 'p' ? 'paragraph' : 'heading') as {
        lineHeight?: string | null;
        spaceAfter?: string | number | null;
        indent?: number | null;
      };
      return {
        block,
        bold: e.isActive('bold'),
        italic: e.isActive('italic'),
        underline: e.isActive('underline'),
        strike: e.isActive('strike'),
        superscript: e.isActive('superscript'),
        subscript: e.isActive('subscript'),
        code: e.isActive('code'),
        link: e.isActive('link'),
        bullet: e.isActive('bulletList'),
        ordered: e.isActive('orderedList'),
        task: e.isActive('taskList'),
        quote: e.isActive('blockquote'),
        codeBlock: e.isActive('codeBlock'),
        table: e.isActive('table'),
        align:
          (['left', 'center', 'right', 'justify'] as const).find((a) =>
            e.isActive({ textAlign: a }),
          ) ?? 'left',
        color: textStyle.color ?? null,
        highlight: (e.getAttributes('highlight') as { color?: string }).color ?? null,
        fontFamily: textStyle.fontFamily?.replace(/["']/g, '') ?? DEFAULT_FONT_FAMILY,
        fontSize: selectionFontSize(e),
        lineHeight: paragraph.lineHeight ?? '',
        spaceAfter:
          paragraph.spaceAfter === null || paragraph.spaceAfter === undefined
            ? ''
            : String(paragraph.spaceAfter),
        indent: paragraph.indent ?? 0,
        canUndo: e.can().undo(),
        canRedo: e.can().redo(),
      };
    },
  });
}

/**
 * The formatting toolbar. On a desktop every group is in view (wrapping onto a second line, never
 * a hidden scroll strip). On a phone only what is used constantly stays on the bar; the rest
 * (fonts, colours, alignment, spacing) opens from one "Format" panel, and the inserts from "Insert".
 */
export function Toolbar({
  editor,
  onImage,
  onFile,
  onFind,
}: {
  editor: Editor;
  onImage: (files: FileList) => void;
  onFile: (files: FileList) => void;
  onFind?: () => void;
}) {
  const { t } = useTranslation();
  const compact = useIsMobile();
  const [linking, setLinking] = useState(false);
  const imageInput = useRef<HTMLInputElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const touch = useIsTouchUi();
  const state = useToolbarState(editor);
  // On a touch screen focus is never forced into the editor by a mere option pick: the on-screen
  // keyboard would rise although the person is only choosing a style.
  const chain = () => (touch && !editor.isFocused ? editor.chain() : editor.chain().focus());
  const settle = () => {
    if (!touch) queueMicrotask(() => editor.commands.focus());
  };
  const tr: Tr = (key, options) =>
    (t as unknown as (k: string, o?: Record<string, unknown>) => string)(
      `documents.editor.${key}`,
      options,
    );

  const blockSelect = (
    <select
      className={styles.select}
      aria-label={tr('blockType')}
      title={tr('blockType')}
      value={state.block}
      data-testid="block-type"
      onChange={(event) => {
        const value = event.target.value;
        if (value === 'p') chain().setParagraph().run();
        else
          chain()
            .setHeading({ level: Number(value.slice(1)) as 1 | 2 | 3 | 4 | 5 | 6 })
            .run();
      }}
    >
      <option value="p">{tr('paragraph')}</option>
      {(['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const).map((value) => (
        <option key={value} value={value}>
          {tr(value)}
        </option>
      ))}
    </select>
  );
  const fontSelect = (
    <select
      className={styles.select}
      aria-label={tr('fontFamily')}
      title={tr('fontFamily')}
      value={state.fontFamily}
      data-testid="font-family"
      onChange={(event) => {
        const value = event.target.value;
        chain().setFontFamily(value).run();
      }}
    >
      {FONT_FAMILIES.map((family) => (
        <option key={family} value={family} style={{ fontFamily: family }}>
          {family}
        </option>
      ))}
    </select>
  );
  const sizeSelect = (
    <FontSizeField
      value={state.fontSize}
      label={tr('fontSize')}
      mixedLabel={tr('fontSizeMixed')}
      invalidLabel={tr('fontSizeInvalid', { min: MIN_FONT_SIZE, max: MAX_FONT_SIZE })}
      onApply={(pt) => {
        chain()
          .setFontSize(`${formatFontSize(pt)}pt`)
          .run();
      }}
    />
  );

  const history = (
    <Group label={tr('groups.history')}>
      <Tool
        label={tr('undo')}
        disabled={!state.canUndo}
        onRun={() => chain().undo().run()}
        testId="tool-undo"
      >
        <ToolIcons.undo />
      </Tool>
      <Tool
        label={tr('redo')}
        disabled={!state.canRedo}
        onRun={() => chain().redo().run()}
        testId="tool-redo"
      >
        <ToolIcons.redo />
      </Tool>
    </Group>
  );
  const basicInline = (
    <>
      <Tool
        label={tr('bold')}
        active={state.bold}
        onRun={() => chain().toggleBold().run()}
        testId="tool-bold"
        wide
      >
        <b>B</b>
      </Tool>
      <Tool
        label={tr('italic')}
        active={state.italic}
        onRun={() => chain().toggleItalic().run()}
        testId="tool-italic"
        wide
      >
        <i>I</i>
      </Tool>
    </>
  );
  const moreInline = (
    <>
      <Tool
        label={tr('underline')}
        active={state.underline}
        onRun={() => chain().toggleUnderline().run()}
        testId="tool-underline"
        wide
      >
        <u>U</u>
      </Tool>
      <Tool
        label={tr('strike')}
        active={state.strike}
        onRun={() => chain().toggleStrike().run()}
        testId="tool-strike"
        wide
      >
        <s>S</s>
      </Tool>
      <Tool
        label={tr('superscript')}
        active={state.superscript}
        onRun={() => chain().toggleMark('superscript').run()}
        testId="tool-superscript"
        wide
      >
        x<sup>2</sup>
      </Tool>
      <Tool
        label={tr('subscript')}
        active={state.subscript}
        onRun={() => chain().toggleMark('subscript').run()}
        testId="tool-subscript"
        wide
      >
        x<sub>2</sub>
      </Tool>
      <Tool
        label={tr('code')}
        active={state.code}
        onRun={() => chain().toggleCode().run()}
        testId="tool-code"
      >
        <ToolIcons.inlineCode />
      </Tool>
      {onFind && (
        <Tool label={tr('find.title')} onRun={onFind} testId="tool-find" wide>
          <span aria-hidden>⌕</span>
        </Tool>
      )}
    </>
  );
  const colors = (
    <>
      <Swatches
        label={tr('color')}
        testId="tool-color"
        colors={COLORS}
        current={state.color}
        onPick={(color) => chain().setColor(color).run()}
        onClear={() => chain().unsetColor().run()}
      >
        <span className={styles.colorMark} style={{ borderColor: state.color ?? 'currentColor' }}>
          A
        </span>
      </Swatches>
      <Swatches
        label={tr('highlight')}
        testId="tool-highlight"
        colors={HIGHLIGHTS}
        current={state.highlight}
        onPick={(color) => chain().toggleHighlight({ color }).run()}
        onClear={() => chain().unsetHighlight().run()}
      >
        <span className={styles.highlightMark} style={{ background: state.highlight ?? '#fff59d' }}>
          A
        </span>
      </Swatches>
    </>
  );
  const alignIcon = {
    left: <ToolIcons.alignLeft />,
    center: <ToolIcons.alignCenter />,
    right: <ToolIcons.alignRight />,
    justify: <ToolIcons.alignJustify />,
  }[state.align];
  const alignMenu = (
    <MenuTool label={tr('alignment')} icon={alignIcon} testId="tool-align" width={220}>
      {(close) => (
        <MenuList>
          {(['left', 'center', 'right', 'justify'] as const).map((align) => (
            <MenuRow key={align}>
              <MenuItem
                icon={
                  {
                    left: <ToolIcons.alignLeft />,
                    center: <ToolIcons.alignCenter />,
                    right: <ToolIcons.alignRight />,
                    justify: <ToolIcons.alignJustify />,
                  }[align]
                }
                aria-pressed={state.align === align}
                trailing={state.align === align ? '✓' : undefined}
                data-testid={`align-${align}`}
                onMouseDown={keepSelection}
                onClick={() => {
                  close();
                  chain().setTextAlign(align).run();
                }}
              >
                {tr(`align.${align}`)}
              </MenuItem>
            </MenuRow>
          ))}
        </MenuList>
      )}
    </MenuTool>
  );
  const spacingMenu = (
    <MenuTool label={tr('spacing')} icon={<ToolIcons.spacing />} testId="tool-spacing" width={260}>
      {(close) => (
        <MenuList>
          <MenuRow>
            <p className={styles.menuHeading}>{tr('lineSpacing')}</p>
          </MenuRow>
          {LINE_HEIGHTS.map((value) => (
            <MenuRow key={value}>
              <MenuItem
                trailing={state.lineHeight === value ? '✓' : undefined}
                data-testid={`line-height-${value}`}
                onMouseDown={keepSelection}
                onClick={() => {
                  close();
                  chain()
                    .setLineHeight(value === '1.15' ? null : value)
                    .run();
                }}
              >
                {value === '1.15' ? tr('spacingDefault') : value}
              </MenuItem>
            </MenuRow>
          ))}
          <MenuRow separated>
            <p className={styles.menuHeading}>{tr('spaceAfter')}</p>
          </MenuRow>
          {SPACE_AFTER.map((value) => (
            <MenuRow key={value}>
              <MenuItem
                trailing={state.spaceAfter === value ? '✓' : undefined}
                data-testid={`space-after-${value}`}
                onMouseDown={keepSelection}
                onClick={() => {
                  close();
                  chain()
                    .setSpaceAfter(value === '6' ? null : value)
                    .run();
                }}
              >
                {value === '6' ? tr('spacingDefault') : `${value} pt`}
              </MenuItem>
            </MenuRow>
          ))}
        </MenuList>
      )}
    </MenuTool>
  );
  const indents = (
    <>
      <Tool
        label={tr('outdent')}
        disabled={state.indent === 0}
        onRun={() => chain().outdentBlock().run()}
        testId="tool-outdent"
      >
        <ToolIcons.outdent />
      </Tool>
      <Tool
        label={tr('indent')}
        disabled={state.indent >= MAX_INDENT}
        onRun={() => chain().indentBlock().run()}
        testId="tool-indent"
      >
        <ToolIcons.indent />
      </Tool>
    </>
  );
  const bulletTool = (
    <Tool
      label={tr('bulletList')}
      active={state.bullet}
      onRun={() => chain().toggleBulletList().run()}
      testId="tool-bullet"
    >
      <ToolIcons.bulletList />
    </Tool>
  );
  const listTools = (
    <>
      <Tool
        label={tr('orderedList')}
        active={state.ordered}
        onRun={() => chain().toggleOrderedList().run()}
        testId="tool-ordered"
      >
        <ToolIcons.orderedList />
      </Tool>
      <Tool
        label={tr('taskList')}
        active={state.task}
        onRun={() => chain().toggleTaskList().run()}
        testId="tool-task"
      >
        <ToolIcons.taskList />
      </Tool>
    </>
  );
  const linkTool = (
    <Tool
      label={tr('link')}
      active={state.link}
      testId="tool-link"
      onRun={() => {
        setLinking(true);
      }}
    >
      <ToolIcons.link />
    </Tool>
  );

  const insertItem = (
    close: () => void,
    icon: ReactNode,
    label: string,
    run: () => void,
    testId: string,
    active?: boolean,
  ) => (
    <MenuRow>
      <MenuItem
        icon={icon}
        aria-pressed={active ?? undefined}
        data-testid={testId}
        onMouseDown={keepSelection}
        onClick={() => {
          close();
          run();
        }}
      >
        {label}
      </MenuItem>
    </MenuRow>
  );
  const insertMenu = (
    <MenuTool label={tr('insert')} icon={<ToolIcons.plus />} testId="tool-insert" width={260}>
      {(close) => (
        <MenuList>
          {insertItem(
            close,
            <ToolIcons.table />,
            tr('insertTable'),
            () => chain().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(),
            'insert-table',
            state.table,
          )}
          {insertItem(
            close,
            <ToolIcons.image />,
            tr('insertImage'),
            () => imageInput.current?.click(),
            'insert-image',
          )}
          {insertItem(
            close,
            <ToolIcons.attachment />,
            tr('insertFile'),
            () => fileInput.current?.click(),
            'insert-file',
          )}
          {insertItem(
            close,
            <ToolIcons.quote />,
            tr('quote'),
            () => chain().toggleBlockquote().run(),
            'insert-quote',
            state.quote,
          )}
          {insertItem(
            close,
            <ToolIcons.codeBlock />,
            tr('codeBlock'),
            () => chain().toggleCodeBlock().run(),
            'insert-code-block',
            state.codeBlock,
          )}
          {insertItem(
            close,
            <ToolIcons.rule />,
            tr('rule'),
            () => chain().setHorizontalRule().run(),
            'insert-rule',
          )}
        </MenuList>
      )}
    </MenuTool>
  );
  // Tables, images and attachments are used often enough to stay one press away on a desktop.
  const directInserts = (
    <>
      <Tool
        label={tr('insertTable')}
        active={state.table}
        testId="tool-table"
        onRun={() => chain().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
      >
        <ToolIcons.table />
      </Tool>
      <Tool label={tr('insertImage')} testId="tool-image" onRun={() => imageInput.current?.click()}>
        <ToolIcons.image />
      </Tool>
      <Tool label={tr('insertFile')} testId="tool-file" onRun={() => fileInput.current?.click()}>
        <ToolIcons.attachment />
      </Tool>
    </>
  );
  const blockMenu = (
    <MenuTool label={tr('moreBlocks')} icon={<ToolIcons.more />} testId="tool-more" width={240}>
      {(close) => (
        <MenuList>
          {insertItem(
            close,
            <ToolIcons.quote />,
            tr('quote'),
            () => chain().toggleBlockquote().run(),
            'tool-quote',
            state.quote,
          )}
          {insertItem(
            close,
            <ToolIcons.codeBlock />,
            tr('codeBlock'),
            () => chain().toggleCodeBlock().run(),
            'tool-code-block',
            state.codeBlock,
          )}
          {insertItem(
            close,
            <ToolIcons.rule />,
            tr('rule'),
            () => chain().setHorizontalRule().run(),
            'tool-rule',
          )}
          {insertItem(
            close,
            <ToolIcons.clear />,
            tr('clearFormatting'),
            () => chain().unsetAllMarks().clearNodes().run(),
            'tool-clear',
          )}
        </MenuList>
      )}
    </MenuTool>
  );

  const files = (
    <>
      <input
        ref={imageInput}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        hidden
        multiple
        data-testid="image-input"
        onChange={(event) => {
          if (event.target.files?.length) onImage(event.target.files);
          event.target.value = '';
        }}
      />
      <input
        ref={fileInput}
        type="file"
        hidden
        multiple
        data-testid="file-input"
        onChange={(event) => {
          if (event.target.files?.length) onFile(event.target.files);
          event.target.value = '';
        }}
      />
    </>
  );

  return (
    <Settle.Provider value={settle}>
      <div className={styles.toolbarHost}>
        <div
          className={styles.toolbar}
          role="toolbar"
          aria-label={tr('toolbar')}
          data-testid="document-toolbar"
          data-compact={compact || undefined}
        >
          {history}
          {compact ? (
            <>
              <Group label={tr('groups.text')}>
                {blockSelect}
                {basicInline}
                {bulletTool}
              </Group>
              <Group label={tr('groups.more')}>
                <Popover
                  label={tr('format')}
                  width={340}
                  alignEnd
                  trigger={(props, open) => (
                    <button
                      type="button"
                      {...props}
                      className={styles.tool}
                      data-wide
                      aria-label={tr('format')}
                      title={tr('format')}
                      data-open={open || undefined}
                      data-testid="tool-format"
                      onMouseDown={keepSelection}
                    >
                      <ToolIcons.fontSize />
                      <ToolIcons.chevron />
                    </button>
                  )}
                >
                  <InPanel.Provider value>
                    <div className={styles.formatPanel} data-testid="format-panel">
                      <div className={styles.panelRow}>
                        {fontSelect}
                        {sizeSelect}
                      </div>
                      <div className={styles.panelRow}>{moreInline}</div>
                      <div className={styles.panelRow}>
                        {listTools}
                        {indents}
                      </div>
                      {colors}
                      {alignMenu}
                      {spacingMenu}
                    </div>
                  </InPanel.Provider>
                </Popover>
                {linkTool}
                {insertMenu}
              </Group>
            </>
          ) : (
            <>
              <Group label={tr('groups.style')}>
                {blockSelect}
                {fontSelect}
                {sizeSelect}
              </Group>
              <Group label={tr('groups.text')}>
                {basicInline}
                {moreInline}
                {colors}
                {linkTool}
              </Group>
              <Group label={tr('groups.paragraph')}>
                {alignMenu}
                {bulletTool}
                {listTools}
                {indents}
                {spacingMenu}
              </Group>
              <Group label={tr('groups.insert')}>
                {directInserts}
                {blockMenu}
              </Group>
            </>
          )}
          {files}
        </div>
        {state.table && <TableBar editor={editor} tr={tr} />}
        {linking && (
          <LinkDialog
            editor={editor}
            onClose={() => {
              setLinking(false);
            }}
          />
        )}
      </div>
    </Settle.Provider>
  );
}
